import {
  ChooseAnswersFieldsGroupAnswerType,
  Color,
  FontWeight,
  formatCurrency,
  Markdown,
  Typography,
  TypographyElement,
  TypographyVariant,
} from '@elearning-platform/ui';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { PageDataContext } from '../../components/page';
import { PageData } from '../../interfaces';

import { InsuranceType } from './enums';
import {
  gapCompensationGross,
  gapCompensationNet,
  gapCompensationNet50,
  unformattedValueAfter30DaysGross,
  unformattedValueAfter30DaysNet,
  unformattedValueAfter30DaysNet50,
  unformattedValueGross,
  unformattedValueNet,
  unformattedValueNet50,
} from './insuranceTable.const';
import {
  CheckboxWrapper,
  Container,
  DescriptionContainer,
  FirstColumnCell,
  GrayTypography,
  HeaderContainer,
  SecondColumnCell,
  StyledCheckboxFieldsGroup,
  StyledCheckboxWrapper,
  StyledDescription,
  StyledMarkdown,
  StyledTypography,
  Table,
  TableCell,
  TableRow,
  TableWrapper,
  TopContainer,
} from './insuranceTable.styled';
import { InsuranceTableProps } from './insuranceTable.types';

export const InsuranceTable: React.FC<InsuranceTableProps> = (props: InsuranceTableProps): JSX.Element => {
  const { callComplete, completed }: InsuranceTableProps = props;
  const { t }: TransProps<never> = useTranslation();
  const dataCtx: Partial<PageData> | null = useContext(PageDataContext);
  const { currentSlideCounter, slidesOverallCounter }: Partial<PageData> = dataCtx ?? {};
  const [gapRtiType, setGapRtiType] = useState<InsuranceType>(InsuranceType.GapRtiGross);
  const [ocAcType, setOcAcType] = useState<InsuranceType>(InsuranceType.OcAcGross);
  const [gapCompensationValue, setGapCompensationValue] = useState<number>(unformattedValueGross - unformattedValueAfter30DaysGross);
  const valueGross: string = formatCurrency(unformattedValueGross, undefined, undefined, true);
  const valueNet50: string = formatCurrency(unformattedValueNet50, undefined, undefined, true);
  const valueNet: string = formatCurrency(unformattedValueNet, undefined, undefined, true);
  const valueAfter30DaysGross: string = formatCurrency(unformattedValueAfter30DaysGross, undefined, undefined, true);
  const valueAfter30DaysNet50: string = formatCurrency(unformattedValueAfter30DaysNet50, undefined, undefined, true);
  const valueAfter30DaysNet: string = formatCurrency(unformattedValueAfter30DaysNet, undefined, undefined, true);

  useEffect((): void => {
    if (!completed) {
      callComplete();
    }
  }, [completed, callComplete]);

  useEffect((): void => {
    if (gapRtiType === InsuranceType.GapRtiGross) {
      setGapCompensationValue(gapCompensationGross);
    } else if (gapRtiType === InsuranceType.GapRtiNet50) {
      setGapCompensationValue(gapCompensationNet50);
    } else {
      setGapCompensationValue(gapCompensationNet);
    }
  }, [gapRtiType]);

  const gapCompensation: string = useMemo((): string => {
    if (gapRtiType === InsuranceType.GapRtiGross) {
      return `${valueGross} - ${valueAfter30DaysGross} = **${formatCurrency(gapCompensationGross, undefined, undefined, true)}**`;
    }
    if (gapRtiType === InsuranceType.GapRtiNet50) {
      return `${valueNet50} - ${valueAfter30DaysNet50} = **${formatCurrency(gapCompensationNet50, undefined, undefined, true)}**`;
    }

    return `${valueNet} - ${valueAfter30DaysNet} = **${formatCurrency(gapCompensationNet, undefined, undefined, true)}**`;
  }, [
    gapRtiType,
    valueNet,
    valueAfter30DaysNet,
    valueAfter30DaysGross,
    valueAfter30DaysNet50,
    valueGross,
    valueNet50,
  ]);

  const totalCompensation: string = useMemo((): string => {
    let ocAcCompensation: number;
    if (ocAcType === InsuranceType.OcAcGross) {
      ocAcCompensation = unformattedValueAfter30DaysGross;
    } else if (ocAcType === InsuranceType.OcAcNet50) {
      ocAcCompensation = unformattedValueAfter30DaysNet50;
    } else {
      ocAcCompensation = unformattedValueAfter30DaysNet;
    }
    const formattedOcAcCompensation: string = formatCurrency(ocAcCompensation, undefined, undefined, true);
    const formattedGapCompensationValue: string = formatCurrency(gapCompensationValue, undefined, undefined, true);
    const result: string = formatCurrency(ocAcCompensation + gapCompensationValue, undefined, undefined, true);

    return `${formattedOcAcCompensation} + ${formattedGapCompensationValue} = **${result}**`;
  }, [ocAcType, gapCompensationValue]);

  const areGapRtiAndOcAcTypesEqual: boolean = useMemo((): boolean => (
    gapRtiType === InsuranceType.GapRtiGross && ocAcType === InsuranceType.OcAcGross
    || gapRtiType === InsuranceType.GapRtiNet && ocAcType === InsuranceType.OcAcNet
    || gapRtiType === InsuranceType.GapRtiNet50 && ocAcType === InsuranceType.OcAcNet50
  ), [gapRtiType, ocAcType]);

  return (
    <Container>
      <HeaderContainer>
        <TopContainer>
          <GrayTypography fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextSM}>
            <Markdown
              text={t('competence.app.scrollLesson.componentsV2.insuranceTable.slides', { currentSlideCounter, slidesOverallCounter })}
            />
          </GrayTypography>
          <GrayTypography fontWeight={FontWeight.Bold} variant={TypographyVariant.TextXS}>
            {t('competence.app.scrollLesson.componentsV2.insuranceTable.interactionLabel')}
          </GrayTypography>
        </TopContainer>
        <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.DisplayXS}>
          {t('competence.app.scrollLesson.componentsV2.insuranceTable.title')}
        </Typography>
      </HeaderContainer>
      <DescriptionContainer>
        <Typography tag={TypographyElement.div} variant={TypographyVariant.TextLG}>
          <StyledDescription text={t('competence.app.scrollLesson.componentsV2.insuranceTable.description')} />
        </Typography>
      </DescriptionContainer>
      <StyledCheckboxWrapper>
        <StyledTypography fontWeight={FontWeight.Bold} variant={TypographyVariant.DisplayXS}>
          {t('competence.app.scrollLesson.componentsV2.insuranceTable.gapRtiLabel')}
        </StyledTypography>
        <StyledCheckboxFieldsGroup
          fields={[
            {
              label: t('competence.app.scrollLesson.componentsV2.insuranceTable.radioButtonLabel.gross'),
              id: InsuranceType.GapRtiGross,
              isSelected: true,
            },
            {
              label: t('competence.app.scrollLesson.componentsV2.insuranceTable.radioButtonLabel.net50'),
              id: InsuranceType.GapRtiNet50,
            },
            {
              label: t('competence.app.scrollLesson.componentsV2.insuranceTable.radioButtonLabel.net'),
              id: InsuranceType.GapRtiNet,
            },
          ]}
          onChange={(selectedOptions: string[]): void => setGapRtiType(selectedOptions[0] as InsuranceType)}
          type={ChooseAnswersFieldsGroupAnswerType.SingleChoice}
        />
      </StyledCheckboxWrapper>
      <CheckboxWrapper>
        <StyledTypography fontWeight={FontWeight.Bold} variant={TypographyVariant.DisplayXS}>
          {t('competence.app.scrollLesson.componentsV2.insuranceTable.ocAcLabel')}
        </StyledTypography>
        <StyledCheckboxFieldsGroup
          fields={[
            {
              label: t('competence.app.scrollLesson.componentsV2.insuranceTable.radioButtonLabel.gross'),
              id: InsuranceType.OcAcGross,
              isSelected: true,
            },
            {
              label: t('competence.app.scrollLesson.componentsV2.insuranceTable.radioButtonLabel.net50'),
              id: InsuranceType.OcAcNet50,
            },
            {
              label: t('competence.app.scrollLesson.componentsV2.insuranceTable.radioButtonLabel.net'),
              id: InsuranceType.OcAcNet,
            },
          ]}
          onChange={(selectedOptions: string[]): void => setOcAcType(selectedOptions[0] as InsuranceType)}
          type={ChooseAnswersFieldsGroupAnswerType.SingleChoice}
        />
      </CheckboxWrapper>
      <TableWrapper>
        <Table>
          <TableRow $bgColor={Color.White}>
            <FirstColumnCell>
              <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.insuranceTable.columnHead.values')}
              </Typography>
            </FirstColumnCell>
            <SecondColumnCell>
              <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.insuranceTable.columnHead.gross')}
              </Typography>
            </SecondColumnCell>
            <TableCell>
              <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.insuranceTable.columnHead.net50')}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.insuranceTable.columnHead.net')}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <FirstColumnCell>
              <Typography variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.insuranceTable.rowHead.vehicleValue')}
              </Typography>
            </FirstColumnCell>
            <SecondColumnCell>
              <Typography variant={TypographyVariant.TextSM}>
                {valueGross}
              </Typography>
            </SecondColumnCell>
            <TableCell>
              <Typography variant={TypographyVariant.TextSM}>
                {valueNet50}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant={TypographyVariant.TextSM}>
                {valueNet}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <FirstColumnCell>
              <Typography variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.insuranceTable.rowHead.vehicleValueAfter30Months')}
              </Typography>
            </FirstColumnCell>
            <SecondColumnCell >
              <Typography variant={TypographyVariant.TextSM}>
                {valueAfter30DaysGross}
              </Typography>
            </SecondColumnCell>
            <TableCell>
              <Typography variant={TypographyVariant.TextSM}>
                {valueAfter30DaysNet50}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant={TypographyVariant.TextSM}>
                {valueAfter30DaysNet}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <FirstColumnCell>
              <Typography variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.insuranceTable.rowHead.acCompensation')}
              </Typography>
            </FirstColumnCell>
            <SecondColumnCell>
              <Typography variant={TypographyVariant.TextSM}>
                {ocAcType === InsuranceType.OcAcGross ? valueAfter30DaysGross : '-'}
              </Typography>
            </SecondColumnCell>
            <TableCell>
              <Typography variant={TypographyVariant.TextSM}>
                {ocAcType === InsuranceType.OcAcNet50 ? valueAfter30DaysNet50 : '-'}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant={TypographyVariant.TextSM}>
                {ocAcType === InsuranceType.OcAcNet ? valueAfter30DaysNet : '-'}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <FirstColumnCell>
              <Typography variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.insuranceTable.rowHead.acEstimatedCompensation')}
              </Typography>
            </FirstColumnCell>
            {areGapRtiAndOcAcTypesEqual ? (
              <>
                <SecondColumnCell $withoutRightBorder>
                  <Typography variant={TypographyVariant.TextSM}>
                    {t('competence.app.scrollLesson.componentsV2.insuranceTable.notApplicable')}
                  </Typography>
                </SecondColumnCell>
                <TableCell $withoutRightBorder />
                <TableCell />
              </>
            ) : (
              <>
                <SecondColumnCell>
                  <Typography variant={TypographyVariant.TextSM}>
                    {gapRtiType === InsuranceType.GapRtiGross ? valueAfter30DaysGross : '-'}
                  </Typography>
                </SecondColumnCell>
                <TableCell>
                  <Typography variant={TypographyVariant.TextSM}>
                    {gapRtiType === InsuranceType.GapRtiNet50 ? valueAfter30DaysNet50 : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant={TypographyVariant.TextSM}>
                    {gapRtiType === InsuranceType.GapRtiNet ? valueAfter30DaysNet : '-'}
                  </Typography>
                </TableCell>
              </>
            )}
          </TableRow>
          <TableRow>
            <FirstColumnCell>
              <Typography variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.insuranceTable.rowHead.gapCompensation')}
              </Typography>
            </FirstColumnCell>
            <SecondColumnCell>
              <Typography variant={TypographyVariant.TextSM}>
                <StyledMarkdown text={gapCompensation} />
              </Typography>
            </SecondColumnCell>
            <TableCell>
              <Typography variant={TypographyVariant.TextSM}>-</Typography>
            </TableCell>
            <TableCell>
              <Typography variant={TypographyVariant.TextSM}>-</Typography>
            </TableCell>
          </TableRow>
          <TableRow $bgColor={Color.LightTurquoise}>
            <FirstColumnCell>
              <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.insuranceTable.rowHead.totalCompensation')}
              </Typography>
            </FirstColumnCell>
            <SecondColumnCell $withoutRightBorder>
              <Typography variant={TypographyVariant.TextSM}>
                <StyledMarkdown text={totalCompensation} />
              </Typography>
            </SecondColumnCell>
            <TableCell $withoutRightBorder />
            <TableCell />
          </TableRow>
        </Table>
      </TableWrapper>
    </Container>
  );
};
